/**
 * OpenCV.js Service
 * 
 * This service handles:
 * - Edge detection for coloring pages
 * - Image processing
 * - Export to PNG
 */

let cvReady = false;
let cvLoading = false;
const cvReadyCallbacks: (() => void)[] = [];

/**
 * Load OpenCV.js asynchronously
 */
export function loadOpenCV(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (cvReady) {
      resolve();
      return;
    }

    if (cvLoading) {
      cvReadyCallbacks.push(resolve);
      return;
    }

    cvLoading = true;

    // Load OpenCV.js from CDN
    const script = document.createElement('script');
    script.src = 'https://docs.opencv.org/4.x/opencv.js';
    script.async = true;
    script.onload = () => {
      // Wait for OpenCV to initialize
      const checkReady = setInterval(() => {
        if ((window as any).cv && (window as any).cv.Mat) {
          clearInterval(checkReady);
          cvReady = true;
          cvLoading = false;
          console.log('[OpenCV] Ready!');
          cvReadyCallbacks.forEach(cb => cb());
          resolve();
        }
      }, 100);
    };
    script.onerror = () => {
      cvLoading = false;
      reject(new Error('Failed to load OpenCV.js'));
    };

    document.head.appendChild(script);
  });
}

/**
 * Check if OpenCV is ready
 */
export function isCVReady(): boolean {
  return cvReady && !!(window as any).cv;
}

/**
 * Get OpenCV instance
 */
function getCV(): any {
  if (!isCVReady()) {
    throw new Error('OpenCV.js is not loaded. Call loadOpenCV() first.');
  }
  return (window as any).cv;
}

/**
 * Extract edges from image using Canny edge detection
 * 
 * @param imageData - Source image data
 * @returns Processed image data with edges
 */
export function extractEdges(imageData: ImageData): ImageData {
  const cv = getCV();
  
  // Create Mat from ImageData
  const src = cv.matFromImageData(imageData);
  const gray = new cv.Mat();
  const edges = new cv.Mat();
  const inverted = new cv.Mat();
  let kernel: any = null;
  
  try {
    // Convert to grayscale
    cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY, 0);
    
    // Apply Gaussian blur to reduce noise
    cv.GaussianBlur(gray, gray, new cv.Size(5, 5), 0);
    
    // Canny edge detection
    cv.Canny(gray, edges, 50, 150);
    
    // Invert colors (black edges on white background)
    cv.threshold(edges, inverted, 50, 255, cv.THRESH_BINARY_INV);
    
    // Optional: Morphological operations to clean up edges
    kernel = cv.getStructuringElement(cv.MORPH_RECT, new cv.Size(3, 3));
    cv.dilate(inverted, inverted, kernel);
    cv.erode(inverted, inverted, kernel);
    
    // Convert back to ImageData
    const result = new ImageData(
      new Uint8ClampedArray(inverted.data),
      inverted.cols,
      inverted.rows
    );
    
    return result;
  } finally {
    // Clean up
    src.delete();
    gray.delete();
    edges.delete();
    inverted.delete();
    if (kernel) kernel.delete();
  }
}

/**
 * Extract edges from canvas
 * 
 * @param canvas - Source canvas
 * @returns New canvas with edge-detected image
 */
export function extractEdgesFromCanvas(canvas: HTMLCanvasElement): HTMLCanvasElement {
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Could not get 2D context from canvas');
  }
  
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const edges = extractEdges(imageData);
  
  const resultCanvas = document.createElement('canvas');
  resultCanvas.width = canvas.width;
  resultCanvas.height = canvas.height;
  const resultCtx = resultCanvas.getContext('2d');
  
  if (!resultCtx) {
    throw new Error('Could not create 2D context for result canvas');
  }
  
  resultCtx.putImageData(edges, 0, 0);
  return resultCanvas;
}

/**
 * Download canvas as PNG
 * 
 * @param canvas - Canvas to download
 * @param filename - Filename (default: 'coloring-page.png')
 */
export function downloadCanvasAsPNG(canvas: HTMLCanvasElement, filename = 'coloring-page.png'): void {
  const link = document.createElement('a');
  link.download = filename;
  link.href = canvas.toDataURL('image/png');
  link.click();
}

/**
 * Create printable version of coloring page
 * Adds white padding for better printing
 * 
 * @param canvas - Source canvas
 * @param padding - Padding in pixels (default: 50)
 * @returns New canvas with padding
 */
export function createPrintableVersion(canvas: HTMLCanvasElement, padding = 50): HTMLCanvasElement {
  const resultCanvas = document.createElement('canvas');
  resultCanvas.width = canvas.width + (padding * 2);
  resultCanvas.height = canvas.height + (padding * 2);
  
  const ctx = resultCanvas.getContext('2d');
  if (!ctx) {
    throw new Error('Could not get 2D context');
  }
  
  // Fill with white background
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, resultCanvas.width, resultCanvas.height);
  
  // Draw original canvas centered
  ctx.drawImage(canvas, padding, padding);
  
  return resultCanvas;
}

/**
 * Apply adaptive thresholding for better edge detection on uneven lighting
 * 
 * @param imageData - Source image data
 * @returns Thresholded image data
 */
export function adaptiveThreshold(imageData: ImageData): ImageData {
  const cv = getCV();
  
  const src = cv.matFromImageData(imageData);
  const gray = new cv.Mat();
  const result = new cv.Mat();
  
  try {
    // Convert to grayscale
    cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY, 0);
    
    // Adaptive thresholding
    cv.adaptiveThreshold(
      gray,
      result,
      255,
      cv.ADAPTIVE_THRESH_GAUSSIAN_C,
      cv.THRESH_BINARY_INV,
      11,
      2
    );
    
    // Convert back to ImageData
    return new ImageData(
      new Uint8ClampedArray(result.data),
      result.cols,
      result.rows
    );
  } finally {
    src.delete();
    gray.delete();
    result.delete();
  }
}

/**
 * Resize image to fit within max dimensions while maintaining aspect ratio
 * 
 * @param canvas - Source canvas
 * @param maxWidth - Maximum width
 * @param maxHeight - Maximum height
 * @returns Resized canvas
 */
export function resizeImage(canvas: HTMLCanvasElement, maxWidth: number, maxHeight: number): HTMLCanvasElement {
  let width = canvas.width;
  let height = canvas.height;
  
  // Calculate new dimensions maintaining aspect ratio
  if (width > height) {
    if (width > maxWidth) {
      height = Math.round((height * maxWidth) / width);
      width = maxWidth;
    }
  } else {
    if (height > maxHeight) {
      width = Math.round((width * maxHeight) / height);
      height = maxHeight;
    }
  }
  
  const resultCanvas = document.createElement('canvas');
  resultCanvas.width = width;
  resultCanvas.height = height;
  
  const ctx = resultCanvas.getContext('2d');
  if (!ctx) {
    throw new Error('Could not get 2D context');
  }
  
  ctx.drawImage(canvas, 0, 0, width, height);
  return resultCanvas;
}

/**
 * Complete flow: Load image, extract edges, and return printable canvas
 * 
 * @param imageOrCanvas - Source image or canvas
 * @param options - Processing options
 * @returns Processed canvas ready for printing
 */
export interface EdgeDetectionOptions {
  maxWidth?: number;
  maxHeight?: number;
  padding?: number;
  useAdaptiveThreshold?: boolean;
}

export async function processImageForColoring(
  imageOrCanvas: HTMLImageElement | HTMLCanvasElement,
  options: EdgeDetectionOptions = {}
): Promise<HTMLCanvasElement> {
  const {
    maxWidth = 2048,
    maxHeight = 2048,
    padding = 50,
    useAdaptiveThreshold = false,
  } = options;
  
  // Create canvas from source
  let sourceCanvas: HTMLCanvasElement;
  
  if (imageOrCanvas instanceof HTMLCanvasElement) {
    sourceCanvas = imageOrCanvas;
  } else {
    sourceCanvas = document.createElement('canvas');
    sourceCanvas.width = imageOrCanvas.naturalWidth;
    sourceCanvas.height = imageOrCanvas.naturalHeight;
    const ctx = sourceCanvas.getContext('2d');
    if (!ctx) throw new Error('Could not get 2D context');
    ctx.drawImage(imageOrCanvas, 0, 0);
  }
  
  // Resize if needed
  const resizedCanvas = resizeImage(sourceCanvas, maxWidth, maxHeight);
  
  // Extract edges
  let processedCanvas: HTMLCanvasElement;
  if (useAdaptiveThreshold) {
    const ctx = resizedCanvas.getContext('2d');
    if (!ctx) throw new Error('Could not get 2D context');
    const imageData = ctx.getImageData(0, 0, resizedCanvas.width, resizedCanvas.height);
    const processed = adaptiveThreshold(imageData);
    processedCanvas = document.createElement('canvas');
    processedCanvas.width = processed.width;
    processedCanvas.height = processed.height;
    const pCtx = processedCanvas.getContext('2d');
    if (!pCtx) throw new Error('Could not get 2D context');
    pCtx.putImageData(processed, 0, 0);
  } else {
    processedCanvas = extractEdgesFromCanvas(resizedCanvas);
  }
  
  // Create printable version with padding
  return createPrintableVersion(processedCanvas, padding);
}
